import React, { useState, useEffect } from 'react';
import { Flame, Trophy, Star, X, Check, ArrowRight, Sparkles, Medal, Target } from 'lucide-react';

// ─── Config per milestone type ───────────────────────────────────────────────
const STREAK_CONFIG = {
  7: {
    gradient: 'from-emerald-500 to-teal-600',
    ringColor: 'ring-emerald-300/40',
    iconBg: 'bg-emerald-400/20',
    iconColor: 'text-emerald-200',
    tag: 'STREAK',
    title: '7 Tage am Stück!',
    subtitle: 'Du bleibst dran. Weiter so!',
    icon: Flame,
    confettiColors: ['#34d399', '#6ee7b7', '#10b981', '#a7f3d0', '#059669'],
    size: 'small',
  },
  30: {
    gradient: 'from-amber-500 to-orange-600',
    ringColor: 'ring-amber-300/40',
    iconBg: 'bg-amber-400/20',
    iconColor: 'text-amber-200',
    tag: 'MEILENSTEIN',
    title: '30 Tage Streak!',
    subtitle: 'Ein ganzer Monat. Unglaublich!',
    icon: Medal,
    confettiColors: ['#fbbf24', '#fcd34d', '#f59e0b', '#fb923c', '#fdba74'],
    size: 'medium',
  },
  60: {
    gradient: 'from-violet-600 to-purple-700',
    ringColor: 'ring-violet-300/40',
    iconBg: 'bg-violet-400/20',
    iconColor: 'text-violet-200',
    tag: 'MEGA STREAK',
    title: '60 Tage Streak!',
    subtitle: 'Du bist kaum aufzuhalten.',
    icon: Trophy,
    confettiColors: ['#a78bfa', '#c4b5fd', '#8b5cf6', '#e879f9', '#f0abfc'],
    size: 'large',
  },
};

const CONFETTI_COUNT = { small: 8, medium: 14, large: 20 };

// ─── Shared confetti animation ────────────────────────────────────────────────
function Confetti({ colors, count }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: i % 3 === 0 ? 10 : 7,
            height: i % 3 === 0 ? 10 : 7,
            backgroundColor: colors[i % colors.length],
            left: `${6 + i * (88 / count)}%`,
            bottom: '20%',
            animation: `confetti-rise ${1.0 + i * 0.08}s ease-out ${i * 0.06}s forwards`,
          }}
        />
      ))}
    </>
  );
}

// ─── Streak milestone modal (7/30/60) ────────────────────────────────────────
function StreakMilestoneContent({ cfg, streak, habitTitle, onClose, count }) {
  const Icon = cfg.icon;
  return (
    <>
      <div className={`relative overflow-hidden bg-gradient-to-br ${cfg.gradient} px-6 pt-8 pb-10 text-center`}>
        <Confetti colors={cfg.confettiColors} count={count} />
        <div className={`trophy-pop w-20 h-20 ${cfg.iconBg} backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ${cfg.ringColor}`}>
          <Icon className={`w-10 h-10 ${cfg.iconColor}`} />
        </div>
        <span className="inline-block px-3 py-1 bg-white/15 text-white/80 text-[11px] font-bold uppercase tracking-widest rounded-full mb-2">
          {cfg.tag}
        </span>
        <h2 className="text-2xl font-black text-white tracking-tight mb-1">{cfg.title}</h2>
        <p className="text-white/65 text-sm">{habitTitle}</p>
      </div>
      <div className="reward-slide px-6 py-5 space-y-4 text-center">
        <p className="text-slate-600 text-sm">{cfg.subtitle}</p>
        <button
          onClick={onClose}
          className={`w-full py-3.5 bg-gradient-to-r ${cfg.gradient} text-white font-bold rounded-2xl active:scale-[0.98] transition-all shadow-lg`}
          data-testid="milestone-close"
        >
          Super, danke!
        </button>
      </div>
    </>
  );
}

// ─── Habit-formed modal (Option A / B) ───────────────────────────────────────
function HabitFormedContent({ habit, daysSinceStart, onClose, onOptionA, onOptionB }) {
  const [phase, setPhase] = useState('celebration'); // 'celebration' | 'choice'

  return phase === 'celebration' ? (
    <>
      <div className="relative overflow-hidden bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 px-6 pt-8 pb-10 text-center">
        <Confetti colors={['#fef08a', '#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#e879f9', '#fb923c']} count={22} />
        {/* Stars */}
        {[...Array(6)].map((_, i) => (
          <Star
            key={i}
            className="absolute text-white/20"
            style={{
              width: 14 + i * 4,
              height: 14 + i * 4,
              top: `${10 + (i * 12) % 40}%`,
              left: `${5 + i * 16}%`,
              transform: `rotate(${i * 30}deg)`,
            }}
          />
        ))}
        <div className="trophy-pop w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-white/30">
          <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
        </div>
        <span className="inline-block px-3 py-1 bg-white/20 text-white/90 text-[11px] font-bold uppercase tracking-widest rounded-full mb-3">
          Gewohnheit aufgebaut
        </span>
        <h2 className="text-2xl font-black text-white tracking-tight mb-1">Ziel erreicht!</h2>
        <p className="text-white/75 text-sm">{habit?.title}</p>
        <p className="text-white/55 text-xs mt-1">{daysSinceStart} Tage · 75 %+ Erfüllungsrate</p>
      </div>

      <div className="reward-slide px-6 py-5 space-y-3">
        <p className="text-slate-600 text-sm text-center leading-relaxed">
          Diese Routine ist fest in deinem Alltag verankert.
          <br />Wie möchtest du weitermachen?
        </p>
        <button
          onClick={() => setPhase('choice')}
          className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-lg shadow-amber-200"
          data-testid="habit-formed-next"
        >
          Weiter <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="w-full py-2 text-xs text-slate-400 hover:text-slate-500 transition-colors">
          Später entscheiden
        </button>
      </div>
    </>
  ) : (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-amber-500 mb-0.5">Deine Wahl</p>
          <h3 className="text-base font-bold text-slate-800">Wie geht es weiter?</h3>
        </div>
        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-3">
        {/* Option A */}
        <button
          onClick={onOptionA}
          className="w-full text-left bg-slate-50 hover:bg-emerald-50 border-2 border-slate-200 hover:border-emerald-300 rounded-2xl p-4 transition-all group active:scale-[0.98]"
          data-testid="habit-formed-option-a"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-emerald-100 group-hover:bg-emerald-200 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
              <Check className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">Option A – Zyklus endet</p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Nur noch Stabilität tracken. Keine Streak-Rewards mehr. Die Routine läuft im Hintergrund weiter.
              </p>
            </div>
          </div>
        </button>

        {/* Option B */}
        <button
          onClick={onOptionB}
          className="w-full text-left bg-slate-50 hover:bg-purple-50 border-2 border-slate-200 hover:border-purple-300 rounded-2xl p-4 transition-all group active:scale-[0.98]"
          data-testid="habit-formed-option-b"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-purple-100 group-hover:bg-purple-200 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 mb-1">Option B – Neue Meilensteine</p>
              <div className="flex gap-2 mt-1.5">
                {[180, 365].map(d => (
                  <span key={d} className="px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-lg">
                    {d} Tage
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mt-1">
                Feiere neue Meilensteine bei 180 und 365 Tagen.
              </p>
            </div>
          </div>
        </button>
      </div>
    </>
  );
}

// ─── Habit anniversary modal (180/365 days) ───────────────────────────────────
function HabitAnniversaryContent({ days, habitTitle, onClose }) {
  const config = days >= 365
    ? { gradient: 'from-yellow-400 via-amber-400 to-orange-500', title: '365 Tage!', subtitle: 'Ein ganzes Jahr. Legendär.', tag: '1 JAHR STREAK' }
    : { gradient: 'from-indigo-600 via-violet-600 to-purple-700', title: '180 Tage!', subtitle: 'Ein halbes Jahr. Außergewöhnlich!', tag: '6 MONATE' };

  return (
    <>
      <div className={`relative overflow-hidden bg-gradient-to-br ${config.gradient} px-6 pt-8 pb-10 text-center`}>
        <Confetti
          colors={days >= 365
            ? ['#fef08a', '#fbbf24', '#f59e0b', '#34d399', '#60a5fa']
            : ['#a78bfa', '#c4b5fd', '#818cf8', '#e879f9', '#60a5fa']}
          count={24}
        />
        <div className="trophy-pop w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-white/30">
          <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
        </div>
        <span className="inline-block px-3 py-1 bg-white/20 text-white/90 text-[11px] font-bold uppercase tracking-widest rounded-full mb-3">
          {config.tag}
        </span>
        <h2 className="text-3xl font-black text-white tracking-tight mb-1">{config.title}</h2>
        <p className="text-white/70 text-sm">{habitTitle}</p>
      </div>
      <div className="reward-slide px-6 py-5 space-y-4 text-center">
        <p className="text-slate-600 text-sm">{config.subtitle}</p>
        <button
          onClick={onClose}
          className={`w-full py-3.5 bg-gradient-to-r ${config.gradient} text-white font-bold rounded-2xl active:scale-[0.98] transition-all shadow-lg`}
          data-testid="milestone-close"
        >
          Absolut legendär!
        </button>
      </div>
    </>
  );
}

// ─── Goal Reached modal (performance habits) ─────────────────────────────────
function GoalReachedContent({ habit, value, onClose, onSetNewGoal }) {
  const [phase, setPhase] = useState('celebration');
  const [newGoal, setNewGoal] = useState('');
  const unit = habit?.tracking?.unitLabel || '';

  return phase === 'celebration' ? (
    <>
      <div className="relative overflow-hidden bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 px-6 pt-8 pb-10 text-center">
        <Confetti colors={['#7dd3fc', '#38bdf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#60a5fa']} count={20} />
        <div className="trophy-pop w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 ring-2 ring-white/30">
          <Target className="w-12 h-12 text-white drop-shadow-lg" />
        </div>
        <span className="inline-block px-3 py-1 bg-white/20 text-white/90 text-[11px] font-bold uppercase tracking-widest rounded-full mb-3">
          Endziel erreicht
        </span>
        <h2 className="text-2xl font-black text-white tracking-tight mb-1">Ziel geschafft!</h2>
        <p className="text-white/70 text-sm">{habit?.title}</p>
        <p className="text-white/55 text-sm mt-1 font-bold">{value} {unit}</p>
      </div>

      <div className="reward-slide px-6 py-5 space-y-3">
        <p className="text-slate-600 text-sm text-center leading-relaxed">
          Du hast dein Endziel erreicht. Willst du ein neues Ziel setzen?
        </p>
        <button
          onClick={() => setPhase('new-goal')}
          className="w-full py-3.5 bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-bold rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-lg shadow-sky-200"
          data-testid="goal-reached-next"
        >
          Neues Ziel setzen <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="w-full py-2 text-xs text-slate-400 hover:text-slate-500 transition-colors">
          Kein neues Ziel
        </button>
      </div>
    </>
  ) : (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-500 mb-0.5">Neues Endziel</p>
          <h3 className="text-base font-bold text-slate-800">{habit?.title}</h3>
        </div>
        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5 space-y-4">
        <div className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3">
          <span className="text-sm text-slate-400 flex-shrink-0">Neues Ziel</span>
          <input
            type="number"
            min={1}
            value={newGoal}
            onChange={e => setNewGoal(e.target.value)}
            placeholder={String((habit?.tracking?.goalTarget || 0) + 10)}
            className="flex-1 px-3 py-2 border border-slate-200 bg-white rounded-xl text-center font-bold text-lg text-slate-800 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
            data-testid="goal-new-target-input"
            autoFocus
          />
          <span className="text-sm text-slate-500 flex-shrink-0">{unit}</span>
        </div>
      </div>
      <div className="px-5 pb-5 space-y-2">
        <button
          onClick={() => newGoal && onSetNewGoal(habit, Number(newGoal))}
          disabled={!newGoal || Number(newGoal) <= 0}
          className={`w-full py-3.5 text-sm font-bold rounded-2xl transition-all flex items-center justify-center gap-2 ${
            newGoal && Number(newGoal) > 0
              ? 'bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200 active:scale-[0.98]'
              : 'bg-slate-100 text-slate-300 cursor-not-allowed'
          }`}
          data-testid="goal-new-target-save"
        >
          <Check className="w-4 h-4" /> Ziel speichern
        </button>
        <button onClick={onClose} className="w-full py-2 text-xs text-slate-400 hover:text-slate-500 transition-colors">
          Überspringen
        </button>
      </div>
    </>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────
export default function AutoMilestoneModal({ milestoneType, habit, daysSinceStart, days, value, onClose, onOptionA, onOptionB, onSetNewGoal }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const streakNum = milestoneType?.startsWith('streak-') ? parseInt(milestoneType.split('-')[1]) : null;
  const cfg = streakNum ? STREAK_CONFIG[streakNum] : null;
  const count = cfg ? CONFETTI_COUNT[cfg.size] : 22;

  return (
    <>
      <style>{`
        @keyframes confetti-rise {
          0%   { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          80%  { opacity: 0.7; }
          100% { transform: translateY(-180px) rotate(520deg) scale(0); opacity: 0; }
        }
        @keyframes trophy-pop {
          0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
          55%  { transform: scale(1.18) rotate(4deg); opacity: 1; }
          75%  { transform: scale(0.95) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes reward-slide-up {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .trophy-pop   { animation: trophy-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        .reward-slide { animation: reward-slide-up 0.45s ease-out forwards; }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{
          backgroundColor: `rgba(0,0,0,${visible ? 0.72 : 0})`,
          backdropFilter: visible ? 'blur(6px)' : 'none',
          transition: 'background-color 0.25s, backdrop-filter 0.25s',
        }}
        onClick={handleClose}
      >
        <div
          className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl"
          style={{
            transform: visible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(40px)',
            opacity: visible ? 1 : 0,
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
          }}
          onClick={e => e.stopPropagation()}
        >
          {cfg && (
            <StreakMilestoneContent
              cfg={cfg}
              streak={streakNum}
              habitTitle={habit?.title}
              onClose={handleClose}
              count={count}
            />
          )}
          {milestoneType === 'habit-formed' && (
            <HabitFormedContent
              habit={habit}
              daysSinceStart={daysSinceStart}
              onClose={handleClose}
              onOptionA={onOptionA}
              onOptionB={onOptionB}
            />
          )}
          {milestoneType === 'habit-anniversary' && (
            <HabitAnniversaryContent
              days={days}
              habitTitle={habit?.title}
              onClose={handleClose}
            />
          )}
          {milestoneType === 'goal-reached' && (
            <GoalReachedContent
              habit={habit}
              value={value}
              onClose={handleClose}
              onSetNewGoal={onSetNewGoal}
            />
          )}
        </div>
      </div>
    </>
  );
}
